/**
 * moon-share.ts — "share the soulmate card to a Telegram chat/contact".
 *
 * Preferred path (Bot API 8.0+): POST the captured JPEG to the bot, which stages
 * a prepared inline photo message (card + «Персональная астрология бесплатно»
 * deep-link); we then trigger the native chat/contact picker with the real
 * image attached.
 *
 * IMPORTANT: we do NOT use `window.Telegram.WebApp.shareMessage`. The app loads
 * the official telegram-web-app.js (which has shareMessage), but @twa-dev/sdk
 * 7.10.1 unconditionally overwrites `window.Telegram.WebApp` with its own bundled
 * 7.10.1 implementation, which has NO shareMessage — so the method is missing on
 * every client (this is also the old fullscreen bug's root). We bypass that by
 * posting the underlying `web_app_send_prepared_message` event straight to the
 * Telegram client via `window.Telegram.WebView.postEvent` (the SDK keeps WebView
 * intact). Support is gated on the client's reported Bot API version (8.0+).
 *
 * Fallback (older clients / any failure): the classic chat picker via
 * t.me/share/url — shares the bot deep-link + text (link preview, no card image).
 *
 * The bot deep-link carries start=share_soulmate → marketing attribution
 * source=share / campaign=soulmate.
 */
import { apiFetch } from './api-client.js';
import { shareLink } from './share.js';

const BOT_SHARE_LINK = 'https://t.me/CalesteaBot?start=share_soulmate';

type TgWebApp = { isVersionAtLeast?: (version: string) => boolean };
type TgWebView = {
  postEvent?: (eventType: string, callback: false | (() => void), data?: unknown) => void;
};

function tg(): { WebApp?: TgWebApp; WebView?: TgWebView } | undefined {
  return (window as unknown as { Telegram?: { WebApp?: TgWebApp; WebView?: TgWebView } }).Telegram;
}

export type MoonShareResult = 'shared' | 'link';

/**
 * Share the captured soulmate card. `linkText` is the localized CTA used only on
 * the link-share fallback (the prepared-message caption is localized server-side).
 */
export async function shareMoonPhaseToChat(
  jpegDataUrl: string,
  jwt: string | null,
  linkText: string,
): Promise<MoonShareResult> {
  const t = tg();
  const postEvent = t?.WebView?.postEvent;
  // The native prepared-message share needs Bot API 8.0+. WebApp.version is set
  // by the client even though the SDK hides the shareMessage method itself.
  const supportsPrepared =
    typeof postEvent === 'function' &&
    typeof t?.WebApp?.isVersionAtLeast === 'function' &&
    t.WebApp.isVersionAtLeast('8.0');

  if (supportsPrepared && jwt) {
    try {
      const res = await apiFetch('/api/v1/sku/moon-phase-soulmate/share-prepare', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ imageBase64: jpegDataUrl }),
      });
      if (res.ok) {
        const { preparedMessageId } = (await res.json()) as { preparedMessageId: string };
        if (preparedMessageId) {
          // Equivalent to WebApp.shareMessage(id) — opens the chat/contact picker
          // with the staged photo message attached.
          postEvent!('web_app_send_prepared_message', false, { id: preparedMessageId });
          return 'shared';
        }
      }
    } catch {
      // fall through to the link share
    }
  }

  // Fallback — native chat picker with the bot link + text (no custom image).
  shareLink(BOT_SHARE_LINK, linkText);
  return 'link';
}
